import React, { useState, useCallback, useMemo, useRef } from 'react';
import { IoMdClose, IoMdCloudUpload } from 'react-icons/io';
import { FaFileExcel, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import { CgSpinner } from 'react-icons/cg';
import { useDispatch } from 'react-redux';
import * as XLSX from 'xlsx';
import { setAppError } from '../../../../../store/userSlice';
import ProjectGroupSection from './ProjectGroupSection';
import ProgressSection from './ProgressSection';
import {
  validateExcelData,
  groupDataByProject,
  mapExcelToApiFormat
} from './excelUtils';

const ExcelUploadPopup = ({
  isOpen,
  onClose,
  onSuccess,
  token
}) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  // State management
  const [uploadState, setUploadState] = useState({
    file: null,
    isUploading: false,
    isDragOver: false
  });

  const [dataState, setDataState] = useState({
    rawData: [],
    groupedData: {},
    validationErrors: [],
    isProcessing: false
  });

  const [mobilizationState, setMobilizationState] = useState({
    isProcessing: false,
    currentProject: null,
    results: [],
    progress: 0
  });

  // File validation
  const validateFile = useCallback((file) => {
    const errors = [];

    // Check file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      '.xlsx',
      '.xls'
    ];

    const isValidType = validTypes.some(type =>
      file.type === type || file.name.toLowerCase().endsWith(type)
    );

    if (!isValidType) {
      errors.push('Please upload a valid Excel file (.xlsx or .xls)');
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      errors.push('File size must be less than 10MB');
    }

    return errors;
  }, []);

  // Excel parsing
  const parseExcelFile = useCallback(async (file) => {
    return new Promise((resolve, reject) => {

      const excelSerialToDate = (serial) => {
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        const date = new Date(excelEpoch.getTime() + serial * 86400000);

        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');

        return `${y}-${m}-${d}`;
      };

      const DATE_COLUMNS = ["MOBILIZATION DATE", "ARRIVAL DATE"];

      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          if (!workbook.SheetNames.includes("Sheet1")) {
            reject(new Error('Excel file must contain a sheet named "Sheet1"'));
            return;
          }

          const worksheet = workbook.Sheets["Sheet1"];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            blankrows: false,
            cellDates: true,
          });

          if (jsonData.length < 2) {
            reject(
              new Error(
                "Excel file must contain at least a header row and one data row"
              )
            );
            return;
          }

          const headers = jsonData[0];
          const rows = jsonData.slice(1);

          const parsedData = rows
            .map((row, index) => {
              const rowData = { _rowIndex: index + 2 };

              headers.forEach((header, colIndex) => {
                const cellValue = row[colIndex];

                if (cellValue === null || cellValue === undefined || cellValue === "") {
                  rowData[header] = "";
                }

                // Handle JS Date objects
                else if (cellValue instanceof Date) {
                  const y = cellValue.getFullYear();
                  const m = String(cellValue.getMonth() + 1).padStart(2, "0");
                  const d = String(cellValue.getDate()).padStart(2, "0");

                  rowData[header] = `${y}-${m}-${d}`;
                }

                // Handle Excel serial date numbers for specific columns
                else if (
                  DATE_COLUMNS.includes(header) &&
                  typeof cellValue === "number"
                ) {
                  rowData[header] = excelSerialToDate(cellValue);
                }

                else {
                  rowData[header] = String(cellValue).trim();
                }
              });

              return rowData;
            })
            .filter((row) => {
              const meaningfulValues = Object.entries(row)
                .filter(([key]) => key !== "_rowIndex")
                .map(([, value]) => value)
                .filter(
                  (value) =>
                    value !== "" &&
                    value !== null &&
                    value !== undefined &&
                    String(value).trim() !== "" &&
                    String(value).toLowerCase() !== "null" &&
                    String(value).toLowerCase() !== "undefined"
                );

              return meaningfulValues.length > 0;
            });

          resolve(parsedData);

        } catch (error) {
          reject(new Error(`Failed to parse Excel file: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsArrayBuffer(file);
    });
  }, []);
  // Handle file selection
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    // Validate file
    const fileErrors = validateFile(file);
    if (fileErrors.length > 0) {
      dispatch(setAppError({
        msg: fileErrors.join(', '),
        color: 'error'
      }));
      return;
    }

    setUploadState(prev => ({ ...prev, file, isUploading: true }));
    setDataState(prev => ({ ...prev, isProcessing: true }));

    try {
      // Parse Excel file
      const rawData = await parseExcelFile(file);

      // Validate data structure
      const validation = validateExcelData(rawData);

      if (validation.errors.length > 0) {
        setDataState(prev => ({
          ...prev,
          validationErrors: validation.errors,
          isProcessing: false
        }));
        setUploadState(prev => ({ ...prev, isUploading: false }));
        return;
      }

      // Map and group data
      const mappedData = mapExcelToApiFormat(validation.validData);
      const groupedData = groupDataByProject(mappedData);

      setDataState({
        rawData: validation.validData,
        groupedData,
        validationErrors: validation.warnings || [],
        isProcessing: false
      });

      setUploadState(prev => ({ ...prev, isUploading: false }));

      dispatch(setAppError({
        msg: `Successfully parsed ${rawData.length} rows, grouped into ${Object.keys(groupedData).length} projects`,
        color: 'success'
      }));

    } catch (error) {
      console.error('Excel parsing error:', error);
      dispatch(setAppError({
        msg: error.message || 'Failed to process Excel file',
        color: 'error'
      }));

      setDataState(prev => ({ ...prev, isProcessing: false }));
      setUploadState(prev => ({ ...prev, isUploading: false, file: null }));
    }
  }, [parseExcelFile, validateFile, dispatch]);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setUploadState(prev => ({ ...prev, isDragOver: true }));
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setUploadState(prev => ({ ...prev, isDragOver: false }));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setUploadState(prev => ({ ...prev, isDragOver: false }));

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  // File input change handler
  const handleFileInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  // Reset handler
  const handleReset = useCallback(() => {
    setUploadState({
      file: null,
      isUploading: false,
      isDragOver: false
    });

    setDataState({
      rawData: [],
      groupedData: {},
      validationErrors: [],
      isProcessing: false
    });

    setMobilizationState({
      isProcessing: false,
      currentProject: null,
      results: [],
      progress: 0
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Close handler
  const handleClose = useCallback(() => {
    if (mobilizationState.isProcessing) {
      const confirmClose = window.confirm(
        'Mobilization is in progress. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }

    handleReset();
    onClose();
  }, [mobilizationState.isProcessing, handleReset, onClose]);

  // Success handler
  const handleMobilizationSuccess = useCallback((results) => {
    const successCount = results.filter(r => r.status === 'success').length;
    const totalCount = results.length;

    dispatch(setAppError({
      msg: `Mobilization completed: ${successCount}/${totalCount} projects processed successfully`,
      color: successCount === totalCount ? 'success' : 'warning'
    }));

    if (onSuccess) {
      onSuccess(results);
    }

    // Auto-close after 3 seconds if all successful
    if (successCount === totalCount) {
      setTimeout(() => {
        handleClose();
      }, 3000);
    }
  }, [dispatch, onSuccess, handleClose]);

  // Computed values
  const hasData = useMemo(() => {
    return Object.keys(dataState.groupedData).length > 0;
  }, [dataState.groupedData]);

  const totalEmployees = useMemo(() => {
    return Object.values(dataState.groupedData).reduce((sum, group) => sum + group.employees.length, 0);
  }, [dataState.groupedData]);

  const projectCount = useMemo(() => {
    return Object.keys(dataState.groupedData).length;
  }, [dataState.groupedData]);

  // Render upload area
  const renderUploadArea = () => (
    <div className="mb-6">
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${uploadState.isDragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
          }
          ${uploadState.isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileInputChange}
          className="hidden"
          disabled={uploadState.isUploading}
        />

        <div className="flex flex-col items-center space-y-4">
          {uploadState.isUploading ? (
            <CgSpinner className="w-12 h-12 text-blue-500 animate-spin" />
          ) : (
            <IoMdCloudUpload className="w-12 h-12 text-gray-400" />
          )}

          <div>
            <p className="text-lg font-medium text-gray-700">
              {uploadState.isUploading ? 'Processing...' : 'Upload Excel File'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Drag and drop or click to select (.xlsx, .xls files only)
            </p>
          </div>

          {uploadState.file && (
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <FaFileExcel className="text-green-600" />
              <span>{uploadState.file.name}</span>
              <span>({(uploadState.file.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
          )}
        </div>
      </div>

      {/* Required columns info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <p>• File must contain a sheet named <strong>"Sheet1"</strong></p>
        <p className="mt-1">• Required columns: <strong>EMPLOYEE ID</strong>, <strong>MOBILIZATION DATE</strong></p>
        <p className="mt-1">• Mobilization date must be &gt;= arrival date</p>
        <p className="mt-1">• Mobilization date should not be &gt; current date</p>
      </div>
    </div>
  );

  // Render validation errors
  const renderValidationErrors = () => {
    if (dataState.validationErrors.length === 0) return null;

    return (
      <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-3">
          <FaExclamationTriangle className="text-red-500" />
          <h4 className="font-medium text-red-800">Validation Errors</h4>
        </div>
        <ul className="space-y-1 text-sm text-red-700">
          {dataState.validationErrors.map((error, index) => (
            <li key={index} className="flex items-start space-x-2">
              <span className="text-red-400 mt-1">•</span>
              <span>{error}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <FaFileExcel className="text-green-600 text-xl" />
            <div>
              <h2 className="text-xl font-semibold text-gray-800">
                Bulk Mobilization Upload
              </h2>
              {hasData && (
                <p className="text-sm text-gray-600">
                  {totalEmployees} employees across {projectCount} projects
                </p>
              )}
            </div>
          </div>

          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={mobilizationState.isProcessing}
          >
            <IoMdClose className="text-xl text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Show progress if mobilization is in progress */}
          {mobilizationState.isProcessing && (
            <ProgressSection
              currentProject={mobilizationState.currentProject}
              results={mobilizationState.results}
              progress={mobilizationState.progress}
            />
          )}

          {/* Show upload area if no data */}
          {!hasData && !dataState.isProcessing && renderUploadArea()}

          {/* Show validation errors */}
          {renderValidationErrors()}

          {/* Show grouped data */}
          {hasData && !mobilizationState.isProcessing && (
            <ProjectGroupSection
              groupedData={dataState.groupedData}
              token={token}
              onSuccess={onSuccess}
              onMobilizationStart={(currentProject) =>
                setMobilizationState(prev => ({ ...prev, currentProject }))
              }
              onMobilizationProgress={(progress) =>
                setMobilizationState(prev => ({ ...prev, progress }))
              }
              onMobilizationComplete={(results) => {
                setMobilizationState(prev => ({
                  ...prev,
                  isProcessing: false,
                  currentProject: null,
                  results,
                  progress: 100
                }));
                handleMobilizationSuccess(results);
              }}
              onMobilizationError={(error) => {
                setMobilizationState(prev => ({
                  ...prev,
                  isProcessing: false,
                  currentProject: null
                }));
                dispatch(setAppError({
                  msg: error.message || 'Mobilization failed',
                  color: 'error'
                }));
              }}
              setMobilizationState={setMobilizationState}
            />
          )}

          {/* Reset button */}
          {(hasData || dataState.validationErrors.length > 0) && !mobilizationState.isProcessing && (
            <div className="mt-6 flex justify-center">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Upload Different File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelUploadPopup;